import React, { useEffect, useReducer } from 'react'

import { RouteConfig } from 'utils/helper'

import { useListenLocalStorage } from './hooks/localStorage'

type Action = { type: 'collapse'; payload?: boolean } | { type: 'initialize' }
type Dispatch = (action: Action) => void
type State = { collapsed?: boolean; routes: RouteConfig[] }
type NavigationProviderProps = {
  children: React.ReactNode
  routes: RouteConfig[]
}

const NavigationStateContext = React.createContext<
  { state: State; dispatch: Dispatch } | undefined
>(undefined)

function navigationReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'collapse': {
      return { ...state, collapsed: action.payload }
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

function NavigationProvider({
  children,
  routes,
}: NavigationProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(navigationReducer, {
    collapsed: localStorage.getItem('mainMenuIsCollapsed')
      ? JSON.parse(localStorage.getItem('mainMenuIsCollapsed'))
      : false,
    routes,
  })

  const localValues = useListenLocalStorage(['mainMenuIsCollapsed'])

  useEffect(() => {
    dispatch({
      type: 'collapse',
      payload:
        localValues.mainMenuIsCollapsed &&
        JSON.parse(localValues.mainMenuIsCollapsed || false),
    })
  }, [localValues.mainMenuIsCollapsed])

  const value = { state, dispatch }

  return (
    <NavigationStateContext.Provider value={value}>
      {children}
    </NavigationStateContext.Provider>
  )
}

function useNavigation(): React.ContextType<typeof NavigationStateContext> {
  const context = React.useContext(NavigationStateContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}

export { NavigationProvider, useNavigation }
